import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { Input } from '@avanta/shared/components/ui/Input';
import { Separator } from '@avanta/shared/components/ui/Separator';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useLogin } from '@avanta/shared/hooks/useLogin';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { AuthService } from 'services/auth';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';
import { loginInputs } from './model';

export const Login = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.AUTH);
  const navigate = useNavigate();
  const { trackUserLogin } = useTrackingEvents();
  const { login, isLoading } = useLogin({
    authenticate: AuthService.login,
    onSuccess: authInfo => {
      trackUserLogin(authInfo.user, authInfo.access_token);
      navigate(ROUTES.ROOT);
    },
  });
  const { register, handleSubmit, watch } = useForm<{ [key: string]: string }>();

  const handleOnSubmit = async (data: { [key: string]: string }): Promise<void> => {
    await login(data.email, data.password);
  };

  const handleForgotPassword = (): void => {
    navigate(ROUTES.RECOVER_PASSWORD, { state: { email: watch('email') } });
  };

  return (
    <div className="flex items-center justify-center bg-gray-50 max-am-xl:mt-24 am-xl:h-screen am-xl:p-24">
      <Card className="w-full bg-white rounded-2xl shadow-md max-w-70 am-sm:max-w-[320px] am-md:max-w-[350px] am-xl:p-10 am-xl:max-w-[380px] am-xl:max-h-[410px]">
        <h2 className="text-2xl font-bold text-center mb-6">{t('Login.heading')}</h2>
        <form onSubmit={handleSubmit(handleOnSubmit)} className="flex flex-col gap-4">
          {Object.entries(loginInputs).map(([formId, input]) => (
            <Input
              key={formId}
              label={t(input.label)}
              type={input.type}
              placeholder={input.placeholder}
              showPassword={input.showPassword}
              autoComplete={input.autocomplete}
              {...register(formId, { required: input.required, pattern: input.pattern?.value })}
            />
          ))}
          <div className="flex justify-end">
            <Button
              variant="link"
              type="button"
              onClick={handleForgotPassword}
              className="text-right p-0 h-fit text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              {t('Login.forgotPassword')}
            </Button>
          </div>
          <div className="flex flex-col gap-4">
            <Button type="submit" className="w-full" isLoading={isLoading}>
              {t('Login.cta')}
            </Button>
            <Separator />
            <Button
              variant="terciary"
              type="button"
              onClick={() => navigate(ROUTES.REGISTER)}
              className="w-full"
            >
              {t('Login.register')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
